<template>
    <div :class="{'no-flex': noFlex}" v-fs-block>
        <template v-if="type === 'date'">
            <div class="field" :class="{'is-error': isInvalid}">
                <label v-if="label" class="field-label">{{label}}</label>
                <v-calendar-manager
                    class="date-input"
                    type="calendar"
                    @input="dateChange"
                    :value="value"
                    :disabled="disabled"
                    :available-dates="availableDates"
                />
                <span class="field-caption" v-if="helpText">{{helpText}}</span>
            </div>
        </template>
        <template v-if="type === 'datetime'">
            <p v-if="label" class="stack-xs">{{label}}</p>
            <div class="grid-fitted grid-unstackable align-items-center" :class="{'is-error': isInvalid}">
                <div class="grid-item inline-s">
                    <v-calendar-manager
                        class="date-input"
                        type="calendar"
                        @input="dateChange"
                        :value="value"
                        :disabled="disabled"
                        :available-dates="availableDates"
                    />
                </div>
                <div class="grid-item no-flex">
                    <v-select
                        :options="timeOptions.hours"
                        :value="selectedTime.hours"
                        placeholder=""
                        :disabled="disabled"
                        @input="updateHours"
                        class="no-flex date-time-input"/>
                </div>
                <div class="grid-item no-flex">
                    <span class="date-time-divider">:</span>
                </div>
                <div class="grid-item no-flex">
                    <v-select
                        :options="timeOptions.minutes"
                        placeholder=""
                        :disabled="disabled"
                        :value="selectedTime.minutes"
                        class="no-flex date-time-input"
                        @input="updateMinutes"
                    />
                </div>
            </div>
            <span class="field-caption" v-if="helpText">{{helpText}}</span>
        </template>
        <template v-if="type === 'time'">
            <p v-if="label" class="stack-xs">{{label}}</p>
            <div class="grid-fitted grid-unstackable align-items-center">
                <div class="grid-item no-flex">
                    <slot
                        :options="timeOptions.hours"
                        :value="selectedTime.hours"
                        :disabled="disabled"
                        :input="updateHours"
                        name="hours-input">
                        <v-select
                            :options="timeOptions.hours"
                            :value="selectedTime.hours"
                            placeholder=""
                            :disabled="disabled"
                            @input="updateHours"
                            class="no-flex date-time-input"/>
                    </slot>
                </div>
                <div class="grid-item no-flex">
                    <span class="date-time-divider">:</span>
                </div>
                <div class="grid-item no-flex">
                    <slot
                        :options="timeOptions.minutes"
                        :value="selectedTime.minutes"
                        :disabled="disabled"
                        :input="updateMinutes"
                        name="minutes-input">
                        <v-select
                            :options="timeOptions.minutes"
                            placeholder=""
                            :disabled="disabled"
                            :value="selectedTime.minutes"
                            class="no-flex date-time-input"
                            @input="updateMinutes"/>
                    </slot>
                </div>
            </div>
            <span class="field-caption" v-if="helpText">{{helpText}}</span>
        </template>
    </div>
</template>

<script>
    import moment from 'moment';
    import vCalendarManager from '@/components/calendar/v-calendar-manager';
    import shortid from 'shortid';
    import vIcon from '@/components/form/v-icon';
    import vSelect from '@/components/form/v-select';

    export default {
        name: 'v-date-input',
        components: {
            vIcon, vCalendarManager, vSelect,
        },
        props: {
            availableDates: {
                type: Object,
                required: false,
                default: null,
            },
            label: {
                type: String,
                default: null,
            },
            disabled: {
                type: Boolean,
                default: false,
            },
            placeholder: {
                type: String,
                default: null,
            },
            helpText: {
                type: String,
                default: null,
            },
            stack: {
                type: String,
                default: null,
            },
            type: {
                type: String,
                default: 'date',
            },
            name: {
                type: String,
                default: () => shortid.generate(),
            },
            value: {
                type: Object,
            },
            validate: {
                type: Object,
                default: null,
            },
            noFlex: {
                type: Boolean,
                required: false,
                default: false,
            },
        },
        data() {
            return {
                selectedDate: this.value,
            };
        },
        computed: {
            isInvalid() {
                if (this.validate) {
                    return this.validate.$invalid && this.validate.$dirty;
                }
                return false;
            },
            timeOptions() {
                /* eslint-disable no-confusing-arrow */
                const hours = Array.from(new Array(24), (val, index) => index < 10 ? `0${index}` : index.toString());
                const minutes = Array.from(new Array(60), (val, index) => index < 10 ? `0${index}` : index.toString());
                /* eslint-enable no-confusing-arrow */
                return {
                    hours,
                    minutes,
                };
            },
            selectedTime() {
                return this.restoreTime(this.value);
            },
        },
        methods: {
            dateChange(date) {
                this.selectedDate = date;
                if (this.value) {
                    this.selectedDate.hours(this.value.hours());
                    this.selectedDate.minutes(this.value.minutes());
                }
                this.$emit('input', this.selectedDate);
            },
            updateTime(type, value) {
                // set empty value
                if (!value) {
                    this.selectedTime.hours = '';
                    this.selectedTime.minutes = '';
                    this.$emit('input', null);
                    return;
                }
                /**
                 * If datepicker would be initialized with null value
                 * this.value is moment, but invalid time.
                 * And, after value changed to null or empty - it's invalid date too.
                 * We should fix that, so, need check value
                 */
                const data = {
                    hours: this.selectedTime.hours || '00',
                    minutes: this.selectedTime.minutes || '00',
                    [type]: value,
                };

                const datetime = this.value && this.value.isValid()
                    ? this.value.clone()
                    : this.$tz().now().startOf('day');

                this.selectedTime.hours = data.hours;
                this.selectedTime.minutes = data.minutes;

                datetime
                    .hours(parseInt(this.selectedTime.hours, 10))
                    .minutes(parseInt(this.selectedTime.minutes, 10));

                this.$emit('input', datetime);
            },
            updateHours(hours) {
                this.updateTime('hours', hours);
            },
            updateMinutes(minutes) {
                this.updateTime('minutes', minutes);
            },
            restoreTime(date) {
                if (!date) {
                    return {
                        hours: '',
                        minutes: '',
                    };
                }
                if (date !== null && moment.isMoment(date)) {
                    return {
                        hours: date.format('HH'),
                        minutes: date.format('mm'),
                    };
                }
                return {
                    hours: '00',
                    minutes: '00',
                };
            },
        },
    };
</script>
